import { Box } from '@mui/material'
import { useMemo, useRef } from 'react'
import {
  AlternativeWorkcentreTableProps,
  AlternativeWorkcentreTableRowType,
} from './AlternativeWorkcenters.types'
import { BaseTable } from 'Features/Table/BaseTable.view'
import { ExtendedGridColDef } from 'Features/Table/BulkUpdateModal'

export const AlternativeWorkcentersMUITable = ({
  data,
  isLoading,
  updateTableData,
  skuCodes,
  workCentreCodes,
  primaryWorkCentreCodes,
}: AlternativeWorkcentreTableProps) => {
  const dataRef = useRef(data)
  dataRef.current = data

  // Handle data updates for BaseTable
  const handleDataUpdate = (
    changedRows: Record<number, AlternativeWorkcentreTableRowType>
  ) => {
    updateTableData(changedRows)
  }

  // Process row function to handle primary workcentre updates
  const processRow = (
    newRow: AlternativeWorkcentreTableRowType,
    oldRow: AlternativeWorkcentreTableRowType
  ): AlternativeWorkcentreTableRowType => {
    const {
      primaryWorkCentreCode,
      workCentreCode,
      productionDate,
      skuCode,
      productionAmount,
    } = newRow

    // Only process if this is an alternative workcentre (not primary)
    if (primaryWorkCentreCode !== workCentreCode) {
      // Find the corresponding primary workcentre row
      const primaryWorkCentreRow = dataRef.current.find(
        (dataRow: AlternativeWorkcentreTableRowType) => {
          return (
            dataRow.workCentreCode === primaryWorkCentreCode &&
            dataRow.skuCode === skuCode &&
            dataRow.productionDate === productionDate &&
            dataRow.primaryWorkCentreCode === primaryWorkCentreCode
          )
        }
      )

      if (primaryWorkCentreRow) {
        const productionAmountDelta = productionAmount - oldRow.productionAmount
        // Update the primary workcentre's production amount
        primaryWorkCentreRow.productionAmount =
          primaryWorkCentreRow.productionAmount - productionAmountDelta
      }
    }

    return newRow
  }

  const columns: ExtendedGridColDef[] = useMemo(
    () => [
      {
        headerName: 'Workcentre code',
        field: 'workCentreCode',
        flex: 1,
        type: 'string',
      },
      {
        headerName: 'Primary workcentre code',
        field: 'primaryWorkCentreCode',
        flex: 1,
        type: 'string',
      },
      {
        headerName: 'SKU code',
        field: 'skuCode',
        flex: 1,
        type: 'string',
      },
      {
        headerName: 'Production date',
        field: 'productionDate',
        flex: 1,
        type: 'string',
      },
      {
        headerName: 'Production amount',
        field: 'productionAmount',
        flex: 1,
        type: 'number',
        editable: true,
        valueFormatter: (value: number) => {
          return value < 0 ? 0 : value
        },
      },
      {
        headerName: 'Total production amount',
        type: 'number',
        field: 'totalProductionAmount',
        flex: 1,
      },
      {
        headerName: 'Run rate',
        type: 'number',
        field: 'runRate',
        flex: 1,
      },
      {
        headerName: 'Production hours',
        type: 'number',
        field: 'productionHours',
        flex: 1,
      },
      {
        headerName: 'Production hours available',
        type: 'number',
        field: 'productionHoursAvailable',
        flex: 1,
      },
      {
        headerName: 'Total workcentre production hours',
        type: 'number',
        field: 'totalWorkCentreProductionHours',
        flex: 1,
      },
    ],
    []
  )
  return (
    <Box>
      <BaseTable
        data={data}
        columns={columns}
        isLoading={isLoading}
        updateTableData={handleDataUpdate}
        processRow={processRow}
        //uploadData={() => {}} // No upload functionality needed
        isUploading={false}
        isSaving={false}
        checkBoxSelection={false}
        showTableToolBar={true}
        downloadFileName="AlternativeWorkcenters"
        initialState={{
          pagination: { paginationModel: { pageSize: 100 } },
        }}
        sx={{
          maxWidth: 'calc(100vw - 70px)',
          '& .MuiDataGrid-cell--editable': {
            backgroundColor: '#CBD5E1',
          },
        }}
        isCellEditable={(params) => {
          // Only allow editing production amount for alternative workcentres (not primary)
          return (
            params.field === 'productionAmount' &&
            params.row.primaryWorkCentreCode !== params.row.workCentreCode
          )
        }}
      />
    </Box>
  )
}
